import React, { useEffect } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { EquipmentCondition } from '@trnsact/trnsact-shared-types';
import { formatStringToNumber } from 'utils/formatters';
import { CurrencyInputField, InputField, SelectField } from 'components/form';
import { classOptions, StandardInfoSingle } from 'modules/equipment';

const requiredText = z
  .string()
  .nullable()
  .refine((value): value is string => !!value && value.trim().length > 0, { message: 'Required field' });

// DR-10802 section 2: what the rep must give to quote a unit the dealer is still looking for.
const prospectiveUnitSchema = z.object({
  year: requiredText,
  make: requiredText,
  model: requiredText,
  modelBase: z.string().nullable(),
  condition: requiredText,
  classType: requiredText,
  retailPrice: z
    .number()
    .nullable()
    .refine((value): value is number => value !== null && value > 0, { message: 'Enter the quoted price' }),
  wholesaleCost: z.number().nullable(),
  mileage: z.number().nullable(),
  hours: z.number().nullable(),
  notes: z.string(),
});

type ProspectiveUnitFormValues = z.input<typeof prospectiveUnitSchema>;

export type ProspectiveUnitValues = z.output<typeof prospectiveUnitSchema>;

const defaultValues: ProspectiveUnitFormValues = {
  year: null,
  make: null,
  model: null,
  modelBase: null,
  condition: null,
  classType: null,
  retailPrice: null,
  wholesaleCost: null,
  mileage: null,
  hours: null,
  notes: '',
};

const prospectiveConditionOptions = [
  { label: 'New', value: EquipmentCondition.New },
  { label: 'Used', value: EquipmentCondition.Used },
];

interface Props {
  isOpen: boolean;
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (values: ProspectiveUnitValues) => void;
}

export const ProspectiveUnitDialog = ({ isOpen, isSaving, onClose, onSubmit }: Props) => {
  const methods = useForm<ProspectiveUnitFormValues>({
    defaultValues,
    resolver: zodResolver(prospectiveUnitSchema),
  });
  const { control, handleSubmit, reset } = methods;

  const condition = useWatch({ control, name: 'condition' });
  const isUsed = condition === EquipmentCondition.Used;

  useEffect(() => {
    if (isOpen) reset(defaultValues);
  }, [isOpen, reset]);

  // The resolver has already validated the values; parse narrows them to the required types.
  const handleSave: SubmitHandler<ProspectiveUnitFormValues> = values => onSubmit(prospectiveUnitSchema.parse(values));

  return (
    <Dialog fullWidth maxWidth="md" open={isOpen} onClose={onClose}>
      <DialogTitle>Add Prospective Unit</DialogTitle>

      <DialogContent dividers>
        <FormProvider {...methods}>
          <Stack gap="1.5rem">
            <Typography variant="body2" color="text.secondary">
              Quote a unit you are still looking for. It is used only on this deal and does not appear in inventory.
              Once the real unit is found, edit it here to add its VIN, stock number and hours or miles.
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} gap="1rem">
              <SelectField
                label="Condition *"
                control={control}
                variant="outlined"
                name="condition"
                options={prospectiveConditionOptions}
              />

              <SelectField
                label="Category *"
                control={control}
                variant="outlined"
                name="classType"
                options={classOptions}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} gap="1rem">
              <StandardInfoSingle />
            </Stack>

            <InputField
              label="Base code / configuration"
              name="modelBase"
              control={control}
              inputProps={{ variant: 'outlined', helperText: 'Enables the base code options for this unit.' }}
            />

            <Stack direction={{ xs: 'column', md: 'row' }} gap="1rem">
              <CurrencyInputField
                control={control}
                label="Quoted price *"
                name="retailPrice"
                textFieldProps={{ variant: 'outlined' }}
              />

              <CurrencyInputField
                control={control}
                label="Estimated cost"
                name="wholesaleCost"
                textFieldProps={{ variant: 'outlined', helperText: 'An estimate. There is no DMS cost yet.' }}
              />
            </Stack>

            {isUsed && (
              <Stack direction={{ xs: 'column', md: 'row' }} gap="1rem">
                <InputField
                  label="Max mileage"
                  control={control}
                  name="mileage"
                  formatter={formatStringToNumber}
                  inputProps={{ type: 'number', variant: 'outlined', helperText: 'Target spec, not a reading.' }}
                />

                <InputField
                  label="Max hours"
                  control={control}
                  name="hours"
                  formatter={formatStringToNumber}
                  inputProps={{ type: 'number', variant: 'outlined', helperText: 'Target spec, not a reading.' }}
                />
              </Stack>
            )}

            <InputField
              control={control}
              label="What we're looking for"
              name="notes"
              inputProps={{ minRows: 3, multiline: true, variant: 'outlined' }}
            />
          </Stack>
        </FormProvider>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>

        <Button variant="contained" onClick={handleSubmit(handleSave)} disabled={isSaving}>
          Add Prospective Unit
        </Button>
      </DialogActions>
    </Dialog>
  );
};
